import React from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { PlaybackCoordinatorProvider } from './contexts/PlaybackCoordinatorContext';
import { PlayerProvider } from './contexts/PlayerContext';
import { AuthProvider } from './contexts/AuthContext';
import { PreviewProvider } from './contexts/PreviewContext';
import AuthCallback from './components/AuthCallback';
import Library from './pages/Library';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminAnalytics from './pages/AdminAnalytics';
import FacilitatorRosterAdmin from './pages/FacilitatorRosterAdmin';
import SoundPackList from './pages/SoundPackList';
import SoundPackBuilder from './pages/SoundPackBuilder';
import AssessmentThemeManager from './pages/AssessmentThemeManager';
import ProgramManager from './pages/ProgramManager';
import AdminMetadataReview from './pages/AdminMetadataReview';
import AdminTaggingStudio from './pages/AdminTaggingStudio';
import StudentReflect from './pages/reflect/StudentReflect';
import TheBooth from './pages/booth/TheBooth';
import ReflectClasses from './pages/reflect/staff/ReflectClasses';
import ReflectClassroom from './pages/reflect/staff/ReflectClassroom';
import ReflectCareInbox from './pages/reflect/staff/ReflectCareInbox';
import { ReflectStaffRoute } from './components/reflect/staff/ReflectStaffShell';
import PreviewPanel from './components/PreviewPanel';
import CratePanel from './components/CratePanel';
import './App.css';

// Router component that handles auth callback detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check for session_id in hash - process synchronously before routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Only show Crate panel on student-facing pages
  const showCratePanel = location.pathname === '/';
  
  return (
    <>
      <Routes>
        <Route path="/" element={<Library />} />
        <Route path="/reflect" element={<StudentReflect />} />
        <Route path="/booth" element={<TheBooth />} />
        <Route path="/reflect/my-work" element={<Navigate to="/reflect" replace />} />
        <Route path="/reflect/shared/:shareCode" element={<Navigate to="/reflect" replace />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route element={<ReflectStaffRoute />}>
          <Route path="/admin/reflect" element={<ReflectClasses />} />
          <Route path="/admin/reflect/classes/:classId" element={<ReflectClassroom />} />
          <Route path="/admin/reflect/alerts" element={<ReflectCareInbox />} />
        </Route>
        <Route path="/admin/programs" element={<ProgramManager />} />
        <Route path="/admin/metadata" element={<AdminMetadataReview />} />
        <Route path="/admin/tagging" element={<AdminTaggingStudio />} />
        <Route path="/admin/assessment-themes" element={<AssessmentThemeManager />} />
        <Route path="/admin/assessment-themes/:theme_id" element={<AssessmentThemeManager />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/facilitator-rosters" element={<FacilitatorRosterAdmin />} />
        <Route path="/admin/sound-packs" element={<SoundPackList />} />
        <Route path="/admin/sound-packs/:pack_id" element={<SoundPackBuilder />} />
      </Routes>
      
      {/* Preview dock — app shell level, persists across all views */}
      <PreviewPanel />
      
      {/* Crate panel — student pages only */}
      {showCratePanel && (
        <CratePanel />
      )}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PlaybackCoordinatorProvider>
          <PlayerProvider>
            <PreviewProvider>
              <div className="App dark">
                <AppRouter />
                <Toaster 
                  position="bottom-right"
                  toastOptions={{
                    style: {
                      background: '#121212',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }
                  }}
                />
              </div>
            </PreviewProvider>
          </PlayerProvider>
        </PlaybackCoordinatorProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
